import { generateSeoMetaTags } from './generateSeoMetaTags'
import type {
  PatternDefinition,
  RouterConfig,
} from '@wix/wix-data-client-common-standalone'
import {
  generate as patternDefGenerate,
  setIndexing as patternDefSetIndexing,
  removeSeoMetaTags,
} from '@wix/wix-data-client-common'
import type { RouterPage } from '@wix/editor-platform-sdk-types'
import type { Schema } from '@wix/wix-data-schema-types'
import type { EDITOR_TYPE } from '@wix/wix-data-client-common'

const findRouteByPageId = ({
  routerPages,
  routerConfig,
  pageId,
}: {
  routerPages: RouterPage[]
  routerConfig: RouterConfig
  pageId: string
}) => {
  const pageRole = routerPages.find(p => p.pageRef.id === pageId)?.pageRoles[0]

  return Object.keys(routerConfig.patterns).find(
    p => routerConfig.patterns[p].pageRole === pageRole,
  )
}

const generateDefinition = ({
  routerConfig,
  route,
  collectionId,
  isIndexable,
  seoV2,
}: {
  routerConfig: RouterConfig
  route: string
  collectionId: string
  isIndexable: boolean
  seoV2: boolean
}) => {
  const routeDefinitionBase = routerConfig.patterns[route]
  const routeDefinition = patternDefGenerate({
    routeDefinitionBase,
    pattern: route,
    collectionId,
    seoV2,
  })

  if (seoV2) {
    return removeSeoMetaTags(routeDefinition)
  }

  return patternDefSetIndexing(routeDefinition, isIndexable)
}

const generateDefinitionAndAdjustToSchema = ({
  routerConfig,
  routerPages,
  route,
  pageId,
  collectionId,
  schema,
  editorType,
  isIndexable,
  seoV2,
}: {
  routerConfig: RouterConfig
  routerPages: RouterPage[]
  route: string
  pageId: string
  collectionId: string
  schema: Schema
  editorType: EDITOR_TYPE
  isIndexable: boolean
  seoV2: boolean
}) => {
  const presetRoute = findRouteByPageId({ routerPages, routerConfig, pageId })
  const routeDefinitionBase = routerConfig.patterns[presetRoute!]

  const routeDefinition = patternDefGenerate({
    routeDefinitionBase,
    pattern: route,
    collectionId,
    seoV2,
  })

  if (seoV2) {
    return removeSeoMetaTags(routeDefinition)
  }

  const seoMetaTags = generateSeoMetaTags({
    collection: schema,
    editorType,
    route,
  })

  const updatedRouteDefinition = {
    ...routeDefinition,
    seoMetaTags,
  } as PatternDefinition

  return patternDefSetIndexing(updatedRouteDefinition, isIndexable)
}

export const generateRouteDefinition = ({
  routerConfig,
  routerPages,
  pageId,
  collectionId,
  schema,
  editorType,
  requiresAdjustingToSchema,
  route,
  isIndexable,
  seoV2,
}: {
  routerConfig: RouterConfig
  routerPages: RouterPage[]
  pageId: string
  collectionId: string
  schema: Schema
  editorType: EDITOR_TYPE
  requiresAdjustingToSchema: boolean
  route: string
  isIndexable: boolean
  seoV2: boolean
}) =>
  requiresAdjustingToSchema
    ? generateDefinitionAndAdjustToSchema({
        routerConfig,
        routerPages,
        route,
        pageId,
        collectionId,
        schema,
        editorType,
        isIndexable,
        seoV2,
      })
    : generateDefinition({
        routerConfig,
        route,
        collectionId,
        isIndexable,
        seoV2,
      })
